import type {
  PageSnapshotSubsystemItem as SdkPageSnapshotSubsystemItem,
  PageSubsystem as SdkPageSubsystem,
  SnapshotSubsystemItem as SdkSnapshotSubsystemItem,
  Subsystem as SdkSubsystem,
} from "@istari/istari-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import clientSDK from "./clientSDK";
import { queryClient } from "./queryClient";

export type SubsystemTaggedConfiguration = {
  id: string;
  systemId: string;
  name: string;
  sha: string;
  archiveStatus: string;
  created: string;
  createdById: string;
};

export type Subsystem = {
  id: string;
  name: string;
  description: string;
  archiveStatus: string;
  tagId: string;
  taggedSnapshotId: string;
  taggedConfiguration: SubsystemTaggedConfiguration;
  infosecLevel?: SdkSubsystem["infosecLevel"];
  createdById: string;
  created: string;
  addedOn?: string;
  addedById?: string;
};

export type SnapshotSubsystem = {
  systemId: string;
  systemName: string;
  systemDescription: string | null;
  tagId: string;
  taggedConfigurationId: string;
  taggedConfigurationName: string;
  taggedSnapshotId: string;
  isArchived: boolean;
  createdById: string;
  created: string;
};

export type TrackedSystem = {
  childSystemId: string;
  specifierType: "Baseline" | "Tag";
  pinnedConfigId?: string;
  pinnedConfigName?: string;
};

export type SystemTreeNodeEdge = {
  parentSystemId: string;
  specifierType: "Baseline" | "Tag";
  pinnedConfigId?: string;
  addedOn?: string;
  addedById?: string;
};

export type ResolvedConfigurationRef = {
  id: string;
  name: string;
  sha: string;
};

export type ResolvedSnapshotRef = {
  id: string;
  created: string;
};

export type SystemTreeNode = {
  nodeRef: string;
  systemId: string;
  name: string;
  description: string;
  edge: SystemTreeNodeEdge;
  hasChildren: boolean;
  resolvedConfiguration: ResolvedConfigurationRef;
  resolvedSnapshot: ResolvedSnapshotRef;
};

type PageRaw<TItem> = {
  items?: TItem[];
  page?: number;
  size?: number;
  total?: number;
};

type Page<TItem> = {
  items: TItem[];
  page?: number;
  size?: number;
  total?: number;
};

type BaselineTagBySystemId = Map<string, string | undefined>;

const toIsoString = (value: Date | string | null | undefined): string => {
  if (!value) return "";
  if (value instanceof Date && Number.isNaN(value.getTime())) return "";
  return value instanceof Date ? value.toISOString() : value;
};

const mapPage = <TRaw, TMapped>(raw: PageRaw<TRaw>, mapItem: (item: TRaw) => TMapped): Page<TMapped> => ({
  ...raw,
  items: (raw.items ?? []).map(mapItem),
});

const mapSubsystemTaggedConfiguration = (raw: SdkSubsystem["taggedConfiguration"]): SubsystemTaggedConfiguration => ({
  id: raw.id,
  systemId: raw.systemId,
  name: raw.name,
  sha: raw.sha,
  archiveStatus: raw.archiveStatus,
  created: toIsoString(raw.created),
  createdById: raw.createdById,
});

export const mapSubsystem = (raw: SdkSubsystem): Subsystem => ({
  id: raw.id,
  name: raw.name,
  description: raw.description ?? "",
  archiveStatus: raw.archiveStatus,
  tagId: raw.tagId,
  taggedSnapshotId: raw.taggedSnapshotId,
  taggedConfiguration: mapSubsystemTaggedConfiguration(raw.taggedConfiguration),
  infosecLevel: raw.infosecLevel ?? undefined,
  createdById: raw.createdById,
  created: toIsoString(raw.created),
  addedOn: toIsoString(raw.addedOn),
  addedById: raw.addedById,
});

export const mapSnapshotSubsystemItem = (raw: SdkSnapshotSubsystemItem): SnapshotSubsystem => ({
  systemId: raw.systemId,
  systemName: raw.systemName,
  systemDescription: raw.systemDescription ?? null,
  tagId: raw.tagId,
  taggedConfigurationId: raw.taggedConfigurationId,
  taggedConfigurationName: raw.taggedConfigurationName,
  taggedSnapshotId: raw.taggedSnapshotId,
  isArchived: raw.isArchived,
  createdById: raw.createdById,
  created: toIsoString(raw.created),
});

const baselineTagQueryOptions = (systemId: string) =>
  queryOptions({
    queryKey: ["system-baseline-tag", systemId] as const,
    queryFn: async () => {
      try {
        const baseline = await clientSDK.getSystemBaseline(systemId);
        return baseline.tagId;
      } catch {
        return undefined;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

const getBaselineTagBySystemId = async (systemIds: string[]): Promise<BaselineTagBySystemId> => {
  const uniqueSystemIds = [...new Set(systemIds)].filter(Boolean);
  const entries = await Promise.all(
    uniqueSystemIds.map(async (systemId) => {
      const tagId = await queryClient.ensureQueryData(baselineTagQueryOptions(systemId));
      return [systemId, tagId] as const;
    }),
  );
  return new Map(entries);
};

const getSpecifierType = (tagId: string | undefined, baselineTagId: string | undefined): "Baseline" | "Tag" =>
  tagId && baselineTagId && tagId === baselineTagId ? "Baseline" : "Tag";

const listConfigurationSubsystems = (configurationId: string, page: number, size: number): Promise<SdkPageSubsystem> =>
  clientSDK.listConfigurationSubsystems(configurationId, page, size);

export const treeKeys = {
  trackedSystems: (configurationId: string) => ["tracked-systems", configurationId] as const,
  snapshotSubsystems: (snapshotId: string) => ["snapshot-subsystems", snapshotId] as const,
  children: (snapshotId: string) => ["system-tree", snapshotId, "children"] as const,
  node: (snapshotId: string, nodeRef: string) => ["system-tree", snapshotId, "node", nodeRef] as const,
};

export const trackedSystemsQueryOptions = (configurationId: string | undefined) =>
  queryOptions({
    queryKey: treeKeys.trackedSystems(configurationId!),
    queryFn: async (): Promise<Page<TrackedSystem>> => {
      const response = await listConfigurationSubsystems(configurationId!, 1, 100);
      const subsystemItems: SdkSubsystem[] = response.items ?? [];
      const baselineTagBySystemId = await getBaselineTagBySystemId(subsystemItems.map((item) => item.id));

      return {
        ...response,
        items: subsystemItems.map((item) => ({
          childSystemId: item.id,
          specifierType: getSpecifierType(item.tagId, baselineTagBySystemId.get(item.id)),
          pinnedConfigId: item.tagId,
          pinnedConfigName: item.taggedConfiguration?.name,
        })),
      };
    },
    enabled: !!configurationId,
    // Tracked systems can change due to create/remove actions; keep this reasonably fresh.
    staleTime: 30_000,
  });

export const useTrackedSystems = (configurationId: string | undefined) =>
  useQuery(trackedSystemsQueryOptions(configurationId));

const mapSnapshotSubsystemToSubsystem = (raw: SnapshotSubsystem): Subsystem => ({
  id: raw.systemId,
  name: raw.systemName,
  description: raw.systemDescription ?? "",
  archiveStatus: raw.isArchived ? "Archived" : "Active",
  taggedConfiguration: {
    id: raw.taggedConfigurationId,
    systemId: raw.systemId,
    name: raw.taggedConfigurationName,
    sha: "",
    archiveStatus: raw.isArchived ? "Archived" : "Active",
    created: raw.created,
    createdById: raw.createdById,
  },
  tagId: raw.tagId,
  taggedSnapshotId: raw.taggedSnapshotId,
  infosecLevel: undefined,
  createdById: raw.createdById,
  created: raw.created,
  addedOn: raw.created,
  addedById: raw.createdById,
});

export const snapshotSubsystemsQueryOptions = (snapshotId: string | undefined) =>
  queryOptions({
    queryKey: treeKeys.snapshotSubsystems(snapshotId!),
    queryFn: async (): Promise<SdkPageSnapshotSubsystemItem> => clientSDK.listSnapshotSubsystems(snapshotId!, 1, 100),
    enabled: !!snapshotId,
    // Snapshot subsystem membership is immutable for a snapshot ID.
    staleTime: Infinity,
  });

export const mapSnapshotSubsystemsToSubsystemPage = (response: SdkPageSnapshotSubsystemItem): Page<Subsystem> => {
  const mapped = mapPage(response, mapSnapshotSubsystemItem);
  return {
    ...mapped,
    items: mapped.items.map(mapSnapshotSubsystemToSubsystem),
  };
};

export const useSnapshotSubsystems = <TData = SdkPageSnapshotSubsystemItem>(
  snapshotId: string | undefined,
  select?: (data: SdkPageSnapshotSubsystemItem) => TData,
) =>
  useQuery({
    ...snapshotSubsystemsQueryOptions(snapshotId),
    select,
  });

const mapSnapshotSubsystemToTreeNode = (raw: SnapshotSubsystem, specifierType: "Baseline" | "Tag"): SystemTreeNode => ({
  nodeRef: raw.systemId,
  systemId: raw.systemId,
  name: raw.systemName,
  description: raw.systemDescription ?? "",
  edge: {
    parentSystemId: "",
    specifierType,
    pinnedConfigId: raw.taggedConfigurationId,
    addedOn: raw.created,
    addedById: raw.createdById,
  },
  hasChildren: true,
  resolvedConfiguration: {
    id: raw.taggedConfigurationId,
    name: raw.taggedConfigurationName,
    sha: "",
  },
  resolvedSnapshot: {
    id: raw.taggedSnapshotId,
    created: raw.created,
  },
});

const getSnapshotSubsystemItems = async (snapshotId: string): Promise<SnapshotSubsystem[]> => {
  const response = await queryClient.ensureQueryData(snapshotSubsystemsQueryOptions(snapshotId));
  return mapPage(response, mapSnapshotSubsystemItem).items;
};

const getTreeNodesForSnapshot = async (snapshotId: string): Promise<SystemTreeNode[]> => {
  const items = await getSnapshotSubsystemItems(snapshotId);
  const baselineTagBySystemId = await getBaselineTagBySystemId(items.map((item) => item.systemId));
  return items.map((item) =>
    mapSnapshotSubsystemToTreeNode(item, getSpecifierType(item.tagId, baselineTagBySystemId.get(item.systemId))),
  );
};

export const useTreeChildren = (snapshotId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: treeKeys.children(snapshotId!),
    queryFn: async () => {
      const items = await getTreeNodesForSnapshot(snapshotId!);
      return {
        items,
        page: 1,
        size: items.length,
        total: items.length,
      };
    },
    enabled: !!snapshotId && enabled,
    // Tree children should update quickly after composition mutations.
    staleTime: 15_000,
  });

export const useTreeNodeContext = (snapshotId: string | undefined, nodeRef: string | undefined) =>
  useQuery({
    queryKey: treeKeys.node(snapshotId!, nodeRef!),
    queryFn: async () => {
      const node = (await getTreeNodesForSnapshot(snapshotId!)).find((item) => item.nodeRef === nodeRef);
      if (!node) throw new Error("Subsystem not found");
      return node;
    },
    enabled: !!snapshotId && !!nodeRef,
    // Node context is derived from immutable snapshot data + cached baseline tags.
    staleTime: 15_000,
  });
